/**
 * Transcript display settings panel, shared by the standalone transcript
 * viewer (/view/transcript, also prebundled into the mobile app) and the live
 * session viewer (/view/session → TranscriptPanel).
 *
 * The two copies had grown apart control by control until MU called the panel
 * "nepřehledné, bez mezer" on a phone (25. 9. 2026): three headed columns that
 * collapsed into one long run on a narrow screen, 8–12 px tall sliders, 32 px
 * swatches and a panel taller than the phone. Now: two headed groups (text,
 * colours), labels always above their control except the bold switch, every
 * control at least 44 px tall, and the panel scrolls inside itself instead of
 * pushing the transcript off screen. Font sizes are unchanged — MU is happy
 * with them.
 *
 * In the app those sizes follow the phone's text size (`--text-scale`), up to
 * 3.6× at iOS's largest accessibility size (MU, 7. 10. 2026). So every label
 * wraps, and the two-up grids fall back to one column once a column would be
 * narrower than ~9 em of text.
 */

import type { ThemedTranscriptSettings, TranscriptViewerSettings } from '@polygraf/shared'
import { Palette, Type, X } from 'lucide-react'
import type { ReactNode } from 'react'

type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * Colour themes offered as one-tap presets. `default` restores `defaults`, whose
 * colours follow the app theme; every other preset is a deliberate pick that
 * stops following it (`colorsFollowTheme: false`), in light and dark alike.
 */
const PRESETS = {
	light: { textColor: '#1e293b', backgroundColor: '#ffffff' },
	dark: { textColor: '#f1f5f9', backgroundColor: '#0f172a' },
	highContrast: { textColor: '#fef3c7', backgroundColor: '#1c1917' },
	sepia: { textColor: '#5c4033', backgroundColor: '#f4ecd8' },
	// Inverted + yellow-on-black: high-luminance-contrast schemes favoured by
	// many partially-sighted readers (bold strokes).
	inverted: { textColor: '#ffffff', backgroundColor: '#000000', fontWeight: 'bold' },
	yellowBlack: { textColor: '#ffe600', backgroundColor: '#000000', fontWeight: 'bold' },
} satisfies Record<string, Partial<TranscriptViewerSettings>>

export type TranscriptPresetKey = keyof typeof PRESETS | 'default'

/** Two columns on a phone at the default text size, one when the text is large. */
const TWO_UP =
	'grid grid-cols-[repeat(auto-fit,minmax(min(100%,calc(9rem*var(--text-scale,1))),1fr))]'

export function TranscriptSettingsPanel({
	settings,
	onChange,
	onClose,
	t,
	defaults,
	presets,
	idPrefix,
	minFontSize,
	maxFontSize,
}: {
	settings: ThemedTranscriptSettings
	onChange: (settings: ThemedTranscriptSettings) => void
	onClose: () => void
	t: Translate
	/** What the `default` preset restores. */
	defaults: ThemedTranscriptSettings
	/** Preset buttons in display order. */
	presets: readonly TranscriptPresetKey[]
	/** Prefix for control ids, kept distinct per surface. */
	idPrefix: string
	minFontSize: number
	maxFontSize: number
}) {
	const color = settings.textColor
	const id = (name: string) => `${idPrefix}-${name}`

	return (
		<div
			className="border-b shrink-0 max-h-[75%] overflow-y-auto overscroll-contain"
			style={{ backgroundColor: settings.backgroundColor, borderColor: `${color}20` }}
			role="dialog"
			aria-label={t('viewer.transcript.settings.title')}
		>
			<div className="max-w-4xl mx-auto px-4 pt-3 pb-5 sm:px-6">
				<div className="flex justify-between items-center gap-3">
					<h2 className="min-w-0 break-words hyphens-auto text-2xl font-semibold" style={{ color }}>
						{t('viewer.transcript.settings.title')}
					</h2>
					<button
						type="button"
						className="-mr-2 min-h-11 min-w-11 rounded-lg flex items-center justify-center hover:bg-black/10 active:bg-black/20 transition-colors"
						style={{ color }}
						onClick={onClose}
						aria-label={t('viewer.transcript.closeSettings')}
					>
						<X size={22} />
					</button>
				</div>

				<div className="mt-3 grid grid-cols-1 md:grid-cols-2 md:gap-x-10">
					<SettingsGroup
						id={id('group-text')}
						icon={<Type size={20} aria-hidden="true" />}
						title={t('viewer.transcript.settings.text')}
						color={color}
					>
						<Field
							id={id('font-size')}
							color={color}
							label={t('viewer.transcript.settings.fontSize', { size: settings.fontSize })}
						>
							<input
								id={id('font-size')}
								type="range"
								min={minFontSize}
								max={maxFontSize}
								value={settings.fontSize}
								onChange={(e) => onChange({ ...settings, fontSize: Number(e.target.value) })}
								className="block w-full h-11 accent-primary cursor-pointer"
							/>
						</Field>
						<Field
							id={id('line-height')}
							color={color}
							label={t('viewer.transcript.settings.lineHeight', {
								height: settings.lineHeight.toFixed(1),
							})}
						>
							<input
								id={id('line-height')}
								type="range"
								min="1"
								max="3"
								step="0.1"
								value={settings.lineHeight}
								onChange={(e) => onChange({ ...settings, lineHeight: Number(e.target.value) })}
								className="block w-full h-11 accent-primary cursor-pointer"
							/>
						</Field>
						<Field
							id={id('font-family')}
							color={color}
							label={t('viewer.transcript.settings.fontFamily')}
						>
							<select
								id={id('font-family')}
								className="block w-full min-h-11 px-3 py-2 rounded-lg border bg-transparent text-xl"
								style={{ color, borderColor: `${color}40` }}
								value={settings.fontFamily}
								onChange={(e) =>
									onChange({
										...settings,
										fontFamily: e.target.value as TranscriptViewerSettings['fontFamily'],
									})
								}
							>
								<option value="sans-serif">Sans-serif</option>
								<option value="serif">Serif</option>
								<option value="monospace">Monospace</option>
								<option value="open-sans">Open Sans</option>
								<option value="roboto">Roboto</option>
								<option value="gelasio">{t('viewer.transcript.settings.fontGelasio')}</option>
								<option value="opendyslexic">OpenDyslexic</option>
							</select>
						</Field>
						{/* The one control whose label sits beside it: a switch reads as
						    "label … on/off", and the whole row is the tap target. */}
						<label className="flex items-center justify-between gap-4 min-h-11 cursor-pointer">
							<span className="min-w-0 break-words hyphens-auto text-xl" style={{ color }}>
								{t('viewer.transcript.settings.fontWeight')}
							</span>
							<input
								type="checkbox"
								className="toggle-refined shrink-0"
								checked={settings.fontWeight === 'bold'}
								onChange={(e) =>
									onChange({ ...settings, fontWeight: e.target.checked ? 'bold' : 'normal' })
								}
							/>
						</label>
					</SettingsGroup>

					<SettingsGroup
						id={id('group-colors')}
						icon={<Palette size={20} aria-hidden="true" />}
						title={t('viewer.transcript.settings.colors')}
						color={color}
						divided
					>
						{/* Side by side on a phone at the default text size; items-end keeps the swatches
						    level when one label wraps to two lines. */}
						<div className={`${TWO_UP} gap-4 items-end`}>
							<Field
								id={id('text-color')}
								color={color}
								label={t('viewer.transcript.settings.textColor')}
							>
								<input
									id={id('text-color')}
									type="color"
									value={settings.textColor}
									onChange={(e) =>
										onChange({ ...settings, textColor: e.target.value, colorsFollowTheme: false })
									}
									className="block w-full h-11 rounded-lg cursor-pointer color-swatch"
									style={{ borderColor: `${color}80` }}
								/>
							</Field>
							<Field
								id={id('background-color')}
								color={color}
								label={t('viewer.transcript.settings.backgroundColor')}
							>
								<input
									id={id('background-color')}
									type="color"
									value={settings.backgroundColor}
									onChange={(e) =>
										onChange({
											...settings,
											backgroundColor: e.target.value,
											colorsFollowTheme: false,
										})
									}
									className="block w-full h-11 rounded-lg cursor-pointer color-swatch"
									style={{ borderColor: `${color}80` }}
								/>
							</Field>
						</div>
						<fieldset className="min-w-0">
							<legend className="break-words hyphens-auto text-xl mb-2" style={{ color }}>
								{t('viewer.transcript.presets.label')}
							</legend>
							<div className={`${TWO_UP} gap-2 sm:flex sm:flex-wrap`}>
								{presets.map((key) => (
									<PresetButton
										key={key}
										label={t(`viewer.transcript.presets.${key}`)}
										onClick={() =>
											onChange(
												key === 'default'
													? defaults
													: { ...settings, ...PRESETS[key], colorsFollowTheme: false },
											)
										}
									/>
								))}
							</div>
						</fieldset>
					</SettingsGroup>
				</div>
			</div>
		</div>
	)
}

/**
 * A headed group of controls. On a phone the groups stack, separated by a
 * rule; from `md` up they sit side by side and the rule goes away.
 */
function SettingsGroup({
	id,
	icon,
	title,
	color,
	divided = false,
	children,
}: {
	id: string
	icon: ReactNode
	title: string
	color: string
	divided?: boolean
	children: ReactNode
}) {
	return (
		<section
			aria-labelledby={id}
			className={`pt-4 ${divided ? 'mt-5 border-t md:mt-0 md:border-t-0' : ''}`}
			style={divided ? { borderColor: `${color}20` } : undefined}
		>
			<h3
				id={id}
				className="flex items-center gap-2 min-w-0 break-words hyphens-auto text-xl font-semibold mb-3"
				style={{ color }}
			>
				{icon}
				{title}
			</h3>
			<div className="space-y-4">{children}</div>
		</section>
	)
}

/** Label above its control. */
function Field({
	id,
	label,
	color,
	children,
}: {
	id: string
	label: string
	color: string
	children: ReactNode
}) {
	return (
		<div>
			<label htmlFor={id} className="block break-words hyphens-auto text-xl mb-1" style={{ color }}>
				{label}
			</label>
			{children}
		</div>
	)
}

/**
 * Preset buttons use the app's own chrome colours, never the viewer's
 * settings: they are the way out of an illegible text/background pair, so
 * they must stay visible whatever the user picked (MP-182).
 */
function PresetButton({ label, onClick }: { label: string; onClick: () => void }) {
	return (
		<button
			type="button"
			className="w-full sm:w-auto min-h-11 px-3.5 py-1.5 break-words hyphens-auto text-lg leading-tight rounded-lg border border-base-300 bg-base-100 text-base-content hover:bg-base-200 active:bg-base-300 transition-colors"
			onClick={onClick}
		>
			{label}
		</button>
	)
}
